import React, { useState } from 'react';
import { toast } from 'sonner';
import { AlertCircleIcon, CheckCircle2Icon, DownloadIcon, FileArchiveIcon, FolderOpenIcon, Loader2Icon, SearchXIcon, Trash2Icon, UploadIcon, XIcon } from 'lucide-react';
import { FilesToolbar } from './FilesToolbar';
import { FilesTable } from './FilesTable';
import { DeleteDialog } from './DeleteDialog';
import { useSmartFiles } from './useSmartFiles';
import { formatSize } from '../../utils/format';
import type { LoanFile, PanelStatus } from '../../types/files';
interface FilesPanelProps {
  initialStatus: PanelStatus;
  compact: boolean;
  emptyFiles: boolean;
}
export function FilesPanel({
  initialStatus,
  compact,
  emptyFiles
}: FilesPanelProps) {
  const smartFiles = useSmartFiles({
    initialStatus,
    emptyFiles
  });
  const [pendingDelete, setPendingDelete] = useState<LoanFile[]>([]);
  const {
    status,
    files,
    visibleFiles,
    availableTypes,
    query,
    setQuery,
    activeTypes,
    toggleType,
    clearFilters,
    selectedIds,
    selectedFiles,
    selectedSizeKb,
    allVisibleSelected,
    someVisibleSelected,
    toggleRow,
    toggleAllVisible,
    clearSelection,
    sort,
    toggleSort,
    downloadStatus,
    downloadProgress,
    lastZipName,
    startZipDownload,
    dismissDownload,
    deleteFiles,
    refresh
  } = smartFiles;
  const isZipping = downloadStatus === 'zipping';
  const hasSelection = selectedIds.length > 0;
  const handleConfirmDelete = () => {
    const ids = pendingDelete.map(file => file.id);
    const count = ids.length;
    deleteFiles(ids);
    setPendingDelete([]);
    toast.success(`${count} ${count === 1 ? 'file' : 'files'} moved to the Recycle Bin`);
  };
  return <section aria-labelledby="files-panel-title" className="rounded bg-white shadow-sm ring-1 ring-slate-200" data-id="element-18" data-mp="1cg60i2hpk7f7">
      <div className="flex flex-col gap-3 border-b border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-19" data-mp="1mf53b4fsl8ua">
        <div className="flex items-center gap-2.5" data-id="element-20" data-mp="wq6nnot7a17c">
          <span className="flex h-8 w-8 items-center justify-center rounded bg-[#eef4ff] text-[#0176d3]" data-id="element-21" data-mp="wovltujbf3iv">
            <FolderOpenIcon className="h-4 w-4" aria-hidden="true" data-id="element-22" data-mp="rq24fr0wqsdf" />
          </span>
          <div data-id="element-23" data-mp="16i43j8v5lhce">
            <h1 id="files-panel-title" className="text-base font-semibold text-slate-900" data-id="element-24" data-mp="16im7ysryz1ve">
              Files ({files.length})
            </h1>
            <p className="text-xs text-slate-500" data-id="element-25" data-mp="1fjkcdc6blbei">
              Select multiple documents to download as a single zip
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-26" data-mp="1pem146woobgk">
          <button type="button" onClick={() => startZipDownload(files.map(file => file.id))} disabled={files.length === 0 || isZipping} className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-[#0176d3] transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-27" data-mp="1kgkfpfbsc01x">
            <FileArchiveIcon className="h-4 w-4" aria-hidden="true" data-id="element-28" data-mp="1s0lchw3qiq66" />
            Download All Files
          </button>
          <button type="button" onClick={() => toast('Upload dialog opens in Salesforce Files')} className="inline-flex items-center gap-1.5 rounded bg-[#0176d3] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-[#014486] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-29" data-mp="1wy4pksop4wwj">
            <UploadIcon className="h-4 w-4" aria-hidden="true" data-id="element-30" data-mp="1dzsgf6xsbn0y" />
            Upload Files
          </button>
        </div>
      </div>

      {downloadStatus !== 'idle' && <div role="status" aria-live="polite" className={['flex items-center gap-3 border-b px-4 py-3 text-sm', downloadStatus === 'complete' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-sky-200 bg-sky-50 text-sky-900'].join(' ')} data-id="element-31" data-mp="33dfbwn2oho9c">
          {isZipping ? <>
              <Loader2Icon className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" data-id="element-32" data-mp="ucayfv432pqs" />
              <div className="min-w-0 flex-1" data-id="element-33" data-mp="pdhhfspl8glv">
                <p className="font-medium" data-id="element-34" data-mp="pfasrlb5bp07">Compressing files into a zip…</p>
                <div className="mt-1.5 h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-sky-200" role="progressbar" aria-valuenow={downloadProgress} aria-valuemin={0} aria-valuemax={100} aria-label="Zip progress" data-id="element-35" data-mp="kky7d5q403lt">
                  <div className="h-full rounded-full bg-[#0176d3] transition-all duration-300" style={{
              width: `${downloadProgress}%`
            }} data-id="element-36" data-mp="1nnb7tzrxqal" />
                </div>
              </div>
            </> : <>
              <CheckCircle2Icon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-37" data-mp="2ibwml3lwc8bx" />
              <p className="min-w-0 flex-1" data-id="element-38" data-mp="2rb9uhrudgouj">
                <span className="font-medium" data-id="element-39" data-mp="2r9yt1yoeft6l">Download ready.</span>{' '}
                <span className="break-all" data-id="element-40" data-mp="1ulk2kuddml4z">{lastZipName}</span> was saved to
                your device.
              </p>
            </>}
          <button type="button" onClick={dismissDownload} aria-label="Dismiss download status" className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded hover:bg-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-41" data-mp="1zh7rd8ny5c9y">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-42" data-mp="1fvcwzf8gwwlp" />
          </button>
        </div>}

      {hasSelection && <div className="flex flex-col gap-2 border-b border-[#0176d3]/30 bg-[#eef4ff] px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between" data-id="element-43" data-mp="2j5gxm2c7bou1">
          <p className="text-sm font-medium text-[#014486]" data-id="element-44" data-mp="2e5l44x14rfsq">
            {selectedIds.length} of {files.length} selected ·{' '}
            {formatSize(selectedSizeKb)}
          </p>
          <div className="flex flex-wrap items-center gap-2" data-id="element-45" data-mp="2ny0kjpws3wbi">
            <button type="button" onClick={() => startZipDownload(selectedIds)} disabled={isZipping} className="inline-flex items-center gap-1.5 rounded bg-[#0176d3] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-[#014486] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-46" data-mp="206uygt08w7sr">
              <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-47" data-mp="3kpc81abqkud2" />
              Download as .zip
            </button>
            <button type="button" onClick={() => setPendingDelete(selectedFiles)} className="inline-flex items-center gap-1.5 rounded border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 transition hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2" data-id="element-48" data-mp="3o5ozlf2j10z8">
              <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-49" data-mp="aqslu1yginkr" />
              Delete
            </button>
            <button type="button" onClick={clearSelection} className="rounded px-2 py-1.5 text-sm font-medium text-[#014486] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-50" data-mp="fpc6lz1bdtbj">
              Clear selection
            </button>
          </div>
        </div>}

      {status === 'ready' && files.length > 0 && <FilesToolbar query={query} onQueryChange={setQuery} activeTypes={activeTypes} typeCounts={availableTypes} onToggleType={toggleType} onClearFilters={clearFilters} onRefresh={refresh} isBusy={false} data-id="element-51" data-mp="ygmzglqlog62" />}

      {status === 'loading' && <LoadingState compact={compact} data-id="element-52" data-mp="2lrpr85bgtk6v" />}

      {status === 'error' && <div className="flex flex-col items-center gap-3 px-4 py-14 text-center" data-id="element-53" data-mp="2gsmjue80j1vq">
          <AlertCircleIcon className="h-8 w-8 text-red-600" aria-hidden="true" data-id="element-54" data-mp="3dhu65ogghf0p" />
          <div data-id="element-55" data-mp="3dfrg54fazlvz">
            <p className="text-sm font-semibold text-slate-900" data-id="element-56" data-mp="39gs6x7mjh2yq">
              We couldn't load files for this record
            </p>
            <p className="mt-1 text-sm text-slate-600" data-id="element-57" data-mp="34g4f4jqhki3g">
              The Files service didn't respond. Your documents are safe — try
              loading them again.
            </p>
          </div>
          <button type="button" onClick={refresh} className="rounded bg-[#0176d3] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-[#014486] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-58" data-mp="1t3203t41c0le">
            Retry
          </button>
        </div>}

      {status === 'ready' && files.length === 0 && <div className="flex flex-col items-center gap-3 px-4 py-14 text-center" data-id="element-59" data-mp="1nvu20z7sklib">
          <FolderOpenIcon className="h-8 w-8 text-slate-400" aria-hidden="true" data-id="element-60" data-mp="1sox27q72qt7h" />
          <div data-id="element-61" data-mp="1wqi8gsp3s98p">
            <p className="text-sm font-semibold text-slate-900" data-id="element-62" data-mp="1woxfv8mcn0dh">
              No files on this loan yet
            </p>
            <p className="mt-1 text-sm text-slate-600" data-id="element-63" data-mp="21myyxtjryzoy">
              Upload income, asset, and property documents to start the
              underwriting checklist.
            </p>
          </div>
          <button type="button" onClick={() => toast('Upload dialog opens in Salesforce Files')} className="inline-flex items-center gap-1.5 rounded bg-[#0176d3] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-[#014486] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-64" data-mp="2bm7xz0thz4i6">
            <UploadIcon className="h-4 w-4" aria-hidden="true" data-id="element-65" data-mp="dnhn6njvcigx" />
            Upload Files
          </button>
        </div>}

      {status === 'ready' && files.length > 0 && visibleFiles.length === 0 && <div className="flex flex-col items-center gap-3 px-4 py-14 text-center" data-id="element-66" data-mp="moo42xqfjvap">
          <SearchXIcon className="h-8 w-8 text-slate-400" aria-hidden="true" data-id="element-67" data-mp="rlobbbz0n43e" />
          <div data-id="element-68" data-mp="wkr33upzjdtf">
            <p className="text-sm font-semibold text-slate-900" data-id="element-69" data-mp="wk9b5aacvlrb">
              No files match your filters
            </p>
            <p className="mt-1 text-sm text-slate-600" data-id="element-70" data-mp="11d2djricqi08">
              Try a different search term or clear the file type filters.
            </p>
          </div>
          <button type="button" onClick={clearFilters} className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-[#0176d3] transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-71" data-mp="1adqlc4h5jm4o">
            Clear filters
          </button>
        </div>}

      {status === 'ready' && visibleFiles.length > 0 && <>
          <div className="overflow-x-auto" data-id="element-72" data-mp="1bg7i80023p2w">
            <FilesTable files={visibleFiles} selectedIds={selectedIds} allVisibleSelected={allVisibleSelected} someVisibleSelected={someVisibleSelected} sort={sort} compact={compact} onToggleRow={toggleRow} onToggleAll={toggleAllVisible} onSort={toggleSort} onDownloadOne={id => startZipDownload([id])} onDeleteOne={file => setPendingDelete([file])} data-id="element-73" data-mp="1bhitekvtzz41" />
          </div>
          <div className="flex items-center justify-between px-4 py-2.5 text-xs text-slate-500" data-id="element-74" data-mp="2qysras2272ce">
            <span data-id="element-75" data-mp="2m4eqga4mo30c">
              Showing {visibleFiles.length} of {files.length} files
            </span>
            <span data-id="element-76" data-mp="kb1wo4c7fkor">
              Total{' '}
              {formatSize(visibleFiles.reduce((total, file) => total + file.sizeKb, 0))}
            </span>
          </div>
        </>}

      {pendingDelete.length > 0 && <DeleteDialog files={pendingDelete} onCancel={() => setPendingDelete([])} onConfirm={handleConfirmDelete} data-id="element-77" data-mp="fgo409oxfrns" />}
    </section>;
}
function LoadingState({
  compact
}: {
  compact: boolean;
}) {
  const rows = compact ? 8 : 6;
  return <div className="divide-y divide-slate-100" aria-busy="true" data-id="element-78" data-mp="3nqhdbof38siv">
      <p className="sr-only" data-id="element-79" data-mp="3sois3x5he5oj">Loading files</p>
      {Array.from({
      length: rows
    }).map((_, index) => <div key={index} className="flex items-center gap-3 px-4 py-3" data-id="element-80" data-mp="1lyazbrqovj1i" data-mpi={index}>
          <div className="h-4 w-4 shrink-0 animate-pulse rounded bg-slate-200" data-id="element-81" data-mp="1lvyhrfoufaoz" />
          <div className="h-5 w-5 shrink-0 animate-pulse rounded bg-slate-200" data-id="element-82" data-mp="1qwmbi94gi5mw" />
          <div className="h-3 flex-1 animate-pulse rounded bg-slate-200" data-id="element-83" data-mp="3bfyfuz0olyk1" />
          <div className="hidden h-3 w-28 animate-pulse rounded bg-slate-200 sm:block" data-id="element-84" data-mp="36glruay2qlfv" />
          <div className="h-3 w-14 animate-pulse rounded bg-slate-200" data-id="element-85" data-mp="3l4q52zecy4r9" />
        </div>)}
    </div>;
}